// Closed vocabularies of the hub. The values match the CHECK constraints in
// supabase/schemas/09_fretum_hub.sql; the names are i18n keys (SelectInput translates them).

export type Choice = { id: string; name: string; color?: string };

export const TEAM_TASK_STATUSES: Choice[] = [
  { id: "todo", name: "hub.status.todo", color: "#94a3b8" },
  { id: "in_progress", name: "hub.status.in_progress", color: "#3b82f6" },
  { id: "blocked", name: "hub.status.blocked", color: "#ef4444" },
  { id: "review", name: "hub.status.review", color: "#a855f7" },
  { id: "done", name: "hub.status.done", color: "#22c55e" },
];

export const PRIORITIES: Choice[] = [
  { id: "low", name: "hub.priority.low", color: "#94a3b8" },
  { id: "medium", name: "hub.priority.medium", color: "#3b82f6" },
  { id: "high", name: "hub.priority.high", color: "#f59e0b" },
  { id: "urgent", name: "hub.priority.urgent", color: "#ef4444" },
];

export const MILESTONE_STATUSES: Choice[] = [
  { id: "pending", name: "hub.status.pending", color: "#94a3b8" },
  { id: "in_progress", name: "hub.status.in_progress", color: "#3b82f6" },
  { id: "done", name: "hub.status.done", color: "#22c55e" },
];

export const PAYMENT_STATUSES: Choice[] = [
  { id: "none", name: "hub.payment.none", color: "#94a3b8" },
  { id: "pending", name: "hub.payment.pending", color: "#f59e0b" },
  { id: "invoiced", name: "hub.payment.invoiced", color: "#3b82f6" },
  { id: "paid", name: "hub.payment.paid", color: "#22c55e" },
];

export const EVENT_KINDS: Choice[] = [
  { id: "meeting", name: "hub.event_kind.meeting" },
  { id: "demo", name: "hub.event_kind.demo" },
  { id: "call", name: "hub.event_kind.call" },
  { id: "internal", name: "hub.event_kind.internal" },
  { id: "deadline", name: "hub.event_kind.deadline" },
  { id: "other", name: "hub.event_kind.other" },
];

export const LINK_KINDS = [
  "repo",
  "staging",
  "production",
  "supabase",
  "cloudflare",
  "design",
  "docs",
  "other",
].map((id) => ({ id, name: `hub.link_kind.${id}` }));

export const STYLE_STATUSES: Choice[] = [
  { id: "idea", name: "hub.styles.status.idea", color: "#94a3b8" },
  { id: "prototype", name: "hub.styles.status.prototype", color: "#f59e0b" },
  { id: "tested", name: "hub.styles.status.tested", color: "#3b82f6" },
  { id: "production", name: "hub.styles.status.production", color: "#22c55e" },
];

export const PROJECT_TYPES = ["crm", "app", "web", "landing"].map((id) => ({
  id,
  name: `hub.styles.project_types.${id}`,
}));

export const WIKI_CATEGORIES = [
  "general",
  "process",
  "technical",
  "client",
  "sales",
  "legal",
].map((id) => ({ id, name: `hub.wiki.categories.${id}` }));

export const SUBSCRIPTION_CATEGORIES = [
  "hosting",
  "domain",
  "software",
  "ai",
  "other",
].map((id) => ({ id, name: `hub.subscriptions.categories.${id}` }));

export const BILLING_PERIODS = ["monthly", "yearly", "one_off"].map((id) => ({
  id,
  name: `hub.subscriptions.periods.${id}`,
}));

export const SUBSCRIPTION_STATUSES = ["active", "cancelled"].map((id) => ({
  id,
  name: `hub.subscriptions.status.${id}`,
}));

export const QUOTE_STATUSES: Choice[] = [
  { id: "draft", name: "hub.quotes.status.draft", color: "#94a3b8" },
  { id: "sent", name: "hub.quotes.status.sent", color: "#3b82f6" },
  { id: "accepted", name: "hub.quotes.status.accepted", color: "#22c55e" },
  { id: "rejected", name: "hub.quotes.status.rejected", color: "#ef4444" },
];

export const INVOICE_STATUSES: Choice[] = [
  { id: "draft", name: "hub.invoices.status.draft", color: "#94a3b8" },
  { id: "issued", name: "hub.invoices.status.issued", color: "#3b82f6" },
  { id: "paid", name: "hub.invoices.status.paid", color: "#22c55e" },
  { id: "overdue", name: "hub.invoices.status.overdue", color: "#ef4444" },
];

export const GDPR_ITEMS = [
  "dpa_signed",
  "supabase_dpa",
  "eu_region",
  "processing_register",
  "privacy_policy",
  "patient_consent",
  "access_control",
  "backups",
  "breach_procedure",
  "data_retention",
] as const;

// Calendar sources: colour and the page each item opens.
export const CALENDAR_SOURCES = {
  event: { color: "#0ea5e9" },
  team_task: { color: "#6366f1" },
  task: { color: "#14b8a6" },
  deal_closing: { color: "#f59e0b" },
  milestone: { color: "#ec4899" },
  renewal: { color: "#64748b" },
  invoice_due: { color: "#ef4444" },
} as const;

export type CalendarSource = keyof typeof CALENDAR_SOURCES;

export const findChoice = <T extends { id: string }>(
  choices: readonly T[],
  id: string | null | undefined,
) => choices.find((choice) => choice.id === id);
